import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Plus, Minus, ChevronLeft, Truck, ShieldCheck, RotateCcw, Star } from "lucide-react";
import { useLang } from "../i18n";
import { useStore } from "../contexts/StoreContext";
import { getProduct, getProducts, getObiReview, formatPrice } from "../lib/api";
import { sanitizeHtml } from "../lib/sanitize";
import { trackViewItem } from "../lib/analytics";
import { Spinner } from "../components/Loaders";
import ProductCard from "../components/ProductCard";
import GpsrInfo from "../components/GpsrInfo";
import { OBI, productImage } from "../assets/obi";

const ProductDetailPage = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [obiReview, setObiReview] = useState(null);
  const [obiLoading, setObiLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    setQty(1);
    setObiReview(null);
    getProduct(id)
      .then((p) => {
        setProduct(p);
        trackViewItem(p);
        const catId = p.categories?.[0]?.id;
        if (catId) {
          getProducts({ category: catId, per_page: 4 })
            .then((r) => setRelated(r.filter((x) => x.id !== p.id).slice(0, 4)))
            .catch(() => {});
        }
        // Fetch OBI review (cached on server)
        setObiLoading(true);
        getObiReview(p.id, lang)
          .then((r) => setObiReview(r.review))
          .catch(() => setObiReview(null))
          .finally(() => setObiLoading(false));
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id, lang]);

  if (loading) return <Spinner />;
  if (!product) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center">
        <img src={OBI.empty} alt="OBI" className="w-44 h-44 mx-auto" />
        <h2 className="font-display font-bold text-3xl mt-6">Prodotto non trovato</h2>
        <Link to="/catalog" className="cartoon-btn bg-yellow-300 mt-6 inline-flex">
          {t("catalog.title")}
        </Link>
      </div>
    );
  }

  const onSale = product.on_sale && product.regular_price && product.sale_price;
  const outOfStock = product.stock_status === "outofstock";
  const images = product.images?.length ? product.images : [{ src: productImage(product, 0), alt: product.name }];
  const rating = parseFloat(product.average_rating || 0);

  return (
    <div data-testid="product-detail-page" className="bg-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/catalog"
          className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-pink-600 mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> {t("catalog.title")}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <div className="bg-pink-100 border-4 border-slate-900 rounded-3xl overflow-hidden cartoon-shadow aspect-square">
              <img
                src={images[activeImg].src}
                alt={images[activeImg].alt || product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-3 mt-4">
                {images.map((img, i) => (
                  <button
                    key={img.id || img.src || i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square bg-white border-4 ${
                      activeImg === i ? "border-pink-500" : "border-slate-900"
                    } rounded-2xl overflow-hidden cartoon-shadow-sm`}
                  >
                    <img src={img.src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              {product.categories?.slice(0, 3).map((c) => (
                <Link
                  key={c.id}
                  to={`/catalog?category=${c.id}`}
                  className="bg-yellow-300 border-2 border-slate-900 rounded-full px-3 py-1 text-xs font-bold hover:bg-yellow-200"
                >
                  {c.name}
                </Link>
              ))}
            </div>

            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              {product.name}
            </h1>

            {rating > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-5 h-5"
                      strokeWidth={2}
                      fill={i < Math.round(rating) ? "#FACC15" : "none"}
                      color={i < Math.round(rating) ? "#FACC15" : "#94A3B8"}
                    />
                  ))}
                </div>
                <span className="text-sm text-slate-600">({product.rating_count})</span>
              </div>
            )}

            <div className="flex items-baseline gap-4">
              {onSale ? (
                <>
                  <span className="font-display font-bold text-4xl text-pink-600">
                    {formatPrice(product.sale_price)}
                  </span>
                  <span className="text-xl line-through text-slate-400">
                    {formatPrice(product.regular_price)}
                  </span>
                </>
              ) : (
                <span className="font-display font-bold text-4xl text-slate-900">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-3 h-3 rounded-full ${
                  outOfStock ? "bg-red-500" : "bg-emerald-500"
                }`}
              />
              <span className="font-bold text-sm">
                {outOfStock ? t("product.out_of_stock") : t("product.in_stock")}
              </span>
            </div>

            {product.short_description && (
              <div
                className="woo-html text-slate-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.short_description) }}
              />
            )}

            {/* Qty + actions */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <div className="flex items-center gap-1 bg-yellow-100 border-4 border-slate-900 rounded-full p-1 cartoon-shadow-sm">
                <button
                  data-testid="qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 bg-white rounded-full border-2 border-slate-900 flex items-center justify-center hover:bg-yellow-200"
                >
                  <Minus className="w-4 h-4" strokeWidth={3} />
                </button>
                <span className="px-3 font-bold w-8 text-center" data-testid="qty-value">{qty}</span>
                <button
                  data-testid="qty-increase"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-9 h-9 bg-white rounded-full border-2 border-slate-900 flex items-center justify-center hover:bg-yellow-200"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} />
                </button>
              </div>

              <button
                data-testid="add-to-cart-detail"
                disabled={outOfStock}
                onClick={() => addToCart(product, qty)}
                className="cartoon-btn bg-pink-400 hover:bg-pink-300 flex-1 sm:flex-none sm:min-w-[220px] text-lg py-3 disabled:opacity-50"
              >
                {t("product.add_to_cart")}
              </button>

              <button
                data-testid="wishlist-detail-btn"
                onClick={() => toggleWishlist(product.id)}
                className="w-12 h-12 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center hover:bg-pink-100 cartoon-shadow-sm"
                aria-label={t("product.add_to_wishlist")}
              >
                <Heart
                  className="w-5 h-5 text-pink-500"
                  strokeWidth={2.5}
                  fill={inWishlist(product.id) ? "#ec4899" : "none"}
                />
              </button>
            </div>

            {/* Trust badges */}
            <div className="grid sm:grid-cols-3 gap-3 pt-4">
              {[
                { Icon: Truck, label: t("home.benefit_1_title") },
                { Icon: ShieldCheck, label: t("home.benefit_2_title") },
                { Icon: RotateCcw, label: "Reso 14gg" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2 bg-white border-2 border-slate-900 rounded-2xl p-3">
                  <Icon className="w-5 h-5 text-sky-600" />
                  <span className="text-xs font-bold">{label}</span>
                </div>
              ))}
            </div>

            {/* OBI mascot tag */}
            <div data-testid="obi-review-box" className="bg-sky-100 border-4 border-slate-900 rounded-2xl p-4 cartoon-shadow-sm">
              <div className="flex items-start gap-3">
                <img src={OBI.dice} alt="OBI" className="w-16 h-16 object-contain shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display font-bold text-base">OBI {lang === "it" ? "ha provato questo gioco!" : "has tried this game!"}</span>
                    <span className="bg-yellow-300 border-2 border-slate-900 rounded-full px-2 py-0.5 text-[10px] font-bold">★ {t("home.obi_badge")}</span>
                  </div>
                  {obiLoading ? (
                    <p className="text-sm text-slate-600 italic">{t("home.obi_loading")}</p>
                  ) : obiReview ? (
                    <p className="text-sm text-slate-800 italic leading-relaxed">“{obiReview}”</p>
                  ) : (
                    <p className="text-sm text-slate-700">
                      {lang === "it"
                        ? "Lo consiglia per serate divertenti in famiglia o tra amici."
                        : "Recommended for fun nights with family or friends."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full description */}
        {product.description && (
          <section className="mt-16">
            <h2 className="font-display font-bold text-3xl mb-4">{t("product.description")}</h2>
            <div
              className="woo-html prose max-w-none bg-white border-4 border-slate-900 rounded-3xl p-6 cartoon-shadow"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
            />
          </section>
        )}

        {/* GPSR product safety info (EU Reg. 2023/988) */}
        {product.brands?.[0] && <GpsrInfo brand={product.brands[0]} />}

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display font-bold text-3xl mb-6">{t("product.related")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
